import type {Config} from '@/modules/common/context/Config/models';
import type {Portal} from '@/types';

import {useMemo} from 'react';

import logoAidu from '@/assets/images/logo-aidu.svg?inline';
import logoInvia from '@/assets/images/logo-invia.svg?inline';
import logoReisegeier from '@/assets/images/logo-reisegeier.svg?inline';
import logoReisenFluege from '@/assets/images/logo-reisen-fluege.svg?inline';
import logoReisen from '@/assets/images/logo-reisen.svg?inline';
import logoTravelplanet from '@/assets/images/logo-travelplanet.svg?inline';
import {useConfig} from '@/modules/common/context/Config';
import {useServiceConfig} from '@/modules/common/hooks/useServiceConfig';

type HeaderConfig = NonNullable<Config['configService']>['headers'][number];

const LOGO_URL_BY_PORTAL: Record<Portal, string> = {
  'aidu-de': logoAidu,
  'aidu-at': logoAidu,
  'aidu-ch': logoAidu,
  'invia-cz': logoInvia,
  'invia-sk': logoInvia,
  'invia-hu': logoInvia,
  'travelplanet-pl': logoTravelplanet,
  'reisegeier-de': logoReisegeier,
  'reisegeier-at': logoReisegeier,
  'reisegeier-ch': logoReisegeier,
  'reisen-de': logoReisen,
  'reisen-fluege-de': logoReisenFluege,
};

const DEFAULT_CONFIG: HeaderConfig = {
  pageType: 'ibe',
  favouriteList: false,
  login: false,
  sslIcon: false,
  logo: {
    alt: import.meta.env.VITE_PORTAL_DOMAIN,
    isClickable: false,
    type: 'url',
    url: LOGO_URL_BY_PORTAL[import.meta.env.VITE_PORTAL],
  },
};

export const useHeaderConfig = (): HeaderConfig => {
  const config = useConfig();
  const [headerConfig] = config.configService?.headers || [];
  const serviceConfig = useServiceConfig();

  return useMemo(() => {
    // While the config is still loading or if it failed to load for whatever reason,
    // we make sure to always return a barebones default config with at least a logo.
    // This also serves as our loading state, since we don't want loading spinners for absolutely essential ui-elements.
    // Otherwise we'd render no header at all!
    if (!headerConfig) {
      return DEFAULT_CONFIG;
    }

    return {
      ...headerConfig,
      // We override the hotlines with the ones from our own service config,
      // because directus isn't flexible enough to provide the data as we need it.
      // @FIXME For now it does not, maybe this can be fixed in the future.
      ...(headerConfig.hotline && {
        hotline: {
          ...headerConfig.hotline,
          numbers: {
            sm: serviceConfig.phone.phone,
            md: serviceConfig.phone.tablet,
            lg: serviceConfig.phone.desktop,
          },
          openingHours: {
            from: serviceConfig.defaultOpeningHours.timeFrom,
            to: serviceConfig.defaultOpeningHours.timeUntil,
          },
        },
      }),
    };
  }, [headerConfig, serviceConfig]);
};
